import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import LegalFooter from '../components/LegalFooter';
import { toggleTheme } from '../lib/theme';
import { 
  Building2, User, ArrowRight, ArrowLeft, Check, Eye, EyeOff, 
  Globe, Mail, Lock, Briefcase, Moon, Sun, UserPlus, ChevronsUpDown, MapPin
} from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { GCC_COUNTRY_CODES, getCountryOptions, getCountryName, isGccCountry } from '../constants/countries';

const GCC_COUNTRIES = GCC_COUNTRY_CODES;

// Strict URL validator: accepts https://example.com, http://example.com,
// example.com, www.example.com, sub.example.co.uk (optional path).
const WEBSITE_URL_REGEX = /^(?:https?:\/\/)?(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(?:\/[^\s]*)?$/;

export default function Signup() {
  const { signup } = useAuth();
  const { t, isRTL, toggleLanguage, language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Capture & persist referral code from ?ref= (survives the 2-step flow)
  const [referralCode, setReferralCode] = useState('');
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      const clean = ref.trim().toUpperCase().slice(0, 16);
      setReferralCode(clean);
      try { sessionStorage.setItem('mid2bid_ref', clean); } catch (e) {}
    } else {
      try {
        const stored = sessionStorage.getItem('mid2bid_ref');
        if (stored) setReferralCode(stored);
      } catch (e) {}
    }
  }, [searchParams]);
  
  // Referral terms (single source of truth — WO-13 B6). Public endpoint.
  const [refTerms, setRefTerms] = useState(null);
  useEffect(() => {
    if (!referralCode) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/referral/terms`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setRefTerms(d))
      .catch(() => {});
  }, [referralCode]);

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [darkMode, setDarkMode] = useState(() => document.documentElement.classList.contains('dark'));
  
  const [formData, setFormData] = useState({
    // Step 1: Personal Information
    user_name: '',
    user_title: '',
    email: '',
    password: '',
    confirmPassword: '',
    // Step 2: Company Information
    company_name: '',
    company_website: '',
    no_company_website: false,
    company_country: '',
    primary_country: '',
    // WO-53 — must be ticked to create the account.
    accept_terms: false
  });
  
  const [errors, setErrors] = useState({});
  const [companyCountryOpen, setCompanyCountryOpen] = useState(false);

  const countryOptions = useMemo(() => getCountryOptions(language), [language]);

  const toggleDarkMode = () => {
    const next = toggleTheme();
    setDarkMode(next === 'dark');
  };

  // Step 1: Validate Personal Information
  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.user_name.trim()) newErrors.user_name = 'Name is required';
    if (!formData.user_title.trim()) newErrors.user_title = 'Job title is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = t.signup.passwordRequirements;
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Step 2: Validate Company Information
  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.company_name.trim()) newErrors.company_name = 'Company name is required';
    // Website is optional ONLY when the "I don't have a valid website" toggle is on
    if (!formData.no_company_website) {
      if (!formData.company_website.trim()) {
        newErrors.company_website = 'Company website is required (or check "I don\'t have a valid website")';
      } else if (!WEBSITE_URL_REGEX.test(formData.company_website.trim())) {
        newErrors.company_website = 'Invalid format. Use https://example.com (or example.com / www.example.com)';
      }
    }
    if (!formData.company_country) newErrors.company_country = 'Please select a country';
    if (!formData.primary_country) newErrors.primary_country = 'Please select a primary country of interest';
    if (!formData.accept_terms) newErrors.accept_terms = isRTL
      ? 'يجب قبول شروط الخدمة لإنشاء الحساب'
      : 'Please accept the Terms of Service to create your account';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep1()) {
      setStep(2);
    }
  };

  const handleBack = () => {
    setStep(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep2()) return;
    
    setLoading(true);
    try {
      const result = await signup({
        company_name: formData.company_name,
        company_website: formData.no_company_website ? '' : formData.company_website,
        no_company_website: formData.no_company_website,
        company_country: formData.company_country,
        primary_country: formData.primary_country,
        user_name: formData.user_name,
        user_title: formData.user_title,
        email: formData.email,
        password: formData.password,
        accept_terms: formData.accept_terms,
        ...(referralCode ? { referral_code: referralCode } : {})
      });
      
      toast.success('Account created! Please check your email for the verification code.');
      try { sessionStorage.removeItem('mid2bid_ref'); } catch (e) {}
      // WO-21 — codeJustSent tells the verify screen a code has only just gone
      // out, so it starts its 60-second Resend countdown. Arriving from the
      // LOGIN screen carries no such flag and the Resend button is live at once.
      navigate('/verify', { state: { email: formData.email, codeJustSent: true } });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      // When user picks a Company Country that is itself a GCC member,
      // default Primary Country of Interest to the same code (only if user
      // hasn't already chosen a primary country, or the previous default
      // mirrored the previous company country).
      if (field === 'company_country') {
        if (isGccCountry(value)) {
          if (!prev.primary_country || prev.primary_country === prev.company_country) {
            next.primary_country = value;
          }
        }
      }
      return next;
    });
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <div className={`min-h-screen bg-background flex items-center justify-center p-4 ${isRTL ? 'font-arabic' : ''}`}>
      {/* Background gradient */}
      <div className="fixed inset-0 bg-gradient-to-br from-primary/5 via-background to-accent/5 pointer-events-none"></div>
      
      {/* Language & Theme Toggle */}
      <div className={`fixed top-4 ${isRTL ? 'left-4' : 'right-4'} flex items-center gap-2 z-50`}>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={toggleLanguage}
          className="rounded-full"
          data-testid="language-toggle"
        >
          {language === 'en' ? 'عربي' : 'EN'}
        </Button>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={toggleDarkMode}
          className="rounded-full"
          data-testid="theme-toggle"
        >
          {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </Button>
      </div>

      <div className="w-full max-w-lg relative z-10">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/login">
            <img src="/logo.png" alt="Mid2Bid" className="h-12 mx-auto" />
          </Link>
        </div>

        {/* Referral banner */}
        {referralCode && (
          <div
            className="mb-6 flex items-center gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3"
            data-testid="referral-banner"
          >
            <UserPlus className="w-5 h-5 text-emerald-500 shrink-0" />
            <p className="text-sm text-emerald-700 dark:text-emerald-300">
              {(() => {
                const pct = refTerms?.referred_discount_percent ?? 15;
                const mo = refTerms?.referred_discount_months ?? 12;
                return language === 'ar'
                  ? `تمت إحالتك! ستحصل على خصم ${pct}٪ لمدة ${mo} أشهر (الرمز: ${referralCode}). يحصل صاحب الرمز على عمولة من Mid2Bid على مدفوعاتك، ولا يغيّر ذلك السعر.`
                  : `You were referred! Enjoy ${pct}% off for ${mo} months (code: ${referralCode}). The person whose code you used receives a commission from Mid2Bid on your payments. This does not change your price.`;
              })()}
            </p>
          </div>
        )}

        {/* Progress indicator */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 1 ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}`}>
                1
              </div>
              <span className={`text-sm font-medium ${step >= 1 ? 'text-primary' : 'text-muted-foreground'}`}>
                {language === 'ar' ? 'معلوماتك' : 'Your Info'}
              </span>
            </div>
            <div className="flex-1 h-0.5 bg-muted mx-4">
              <div className={`h-full transition-all ${step >= 2 ? 'bg-primary w-full' : 'w-0'}`}></div>
            </div>
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 2 ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}`}>
                2
              </div>
              <span className={`text-sm font-medium ${step >= 2 ? 'text-primary' : 'text-muted-foreground'}`}>
                {language === 'ar' ? 'معلومات الشركة' : 'Company Info'}
              </span>
            </div>
          </div>
          <Progress value={step === 1 ? 50 : 100} className="h-2" />
        </div>

        <Card className="border-2 shadow-xl">
          <CardHeader className="text-center pb-4">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
              {step === 1 ? (
                <User className="w-8 h-8 text-white" />
              ) : (
                <Building2 className="w-8 h-8 text-white" />
              )}
            </div>
            <CardTitle className="text-2xl font-bold" style={{fontFamily: "'Sora', sans-serif"}}>
              {step === 1 
                ? (language === 'ar' ? 'معلوماتك الشخصية' : 'Your Personal Information')
                : (language === 'ar' ? 'معلومات الشركة' : 'Company Information')
              }
            </CardTitle>
            <CardDescription>
              {step === 1 
                ? (language === 'ar' ? 'أخبرنا عن نفسك' : 'Tell us about yourself')
                : (language === 'ar' ? 'أخبرنا عن شركتك' : 'Tell us about your company')
              }
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={step === 2 ? handleSubmit : (e) => { e.preventDefault(); handleNext(); }}>
              {step === 1 ? (
                /* Step 1: Personal Information */
                <div className="space-y-4">
                  {/* User Name */}
                  <div className="space-y-2">
                    <Label htmlFor="user_name" className={isRTL ? 'text-right block' : ''}>
                      {t.signup.yourName}
                    </Label>
                    <div className="relative">
                      <User className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} w-5 h-5 text-muted-foreground`} />
                      <Input
                        id="user_name"
                        placeholder={t.signup.yourNamePlaceholder}
                        value={formData.user_name}
                        onChange={(e) => updateField('user_name', e.target.value)}
                        className={`${isRTL ? 'pr-10 text-right' : 'pl-10'} h-12`}
                        data-testid="user-name-input"
                      />
                    </div>
                    {errors.user_name && <p className="text-sm text-destructive">{errors.user_name}</p>}
                  </div>

                  {/* Job Title */}
                  <div className="space-y-2">
                    <Label htmlFor="user_title" className={isRTL ? 'text-right block' : ''}>
                      {t.signup.yourTitle}
                    </Label>
                    <div className="relative">
                      <Briefcase className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} w-5 h-5 text-muted-foreground`} />
                      <Input
                        id="user_title"
                        placeholder={t.signup.yourTitlePlaceholder}
                        value={formData.user_title}
                        onChange={(e) => updateField('user_title', e.target.value)}
                        className={`${isRTL ? 'pr-10 text-right' : 'pl-10'} h-12`}
                        data-testid="user-title-input"
                      />
                    </div>
                    {errors.user_title && <p className="text-sm text-destructive">{errors.user_title}</p>}
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="email" className={isRTL ? 'text-right block' : ''}>
                      {t.signup.email}
                    </Label>
                    <div className="relative">
                      <Mail className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} w-5 h-5 text-muted-foreground`} />
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        placeholder={t.signup.emailPlaceholder}
                        value={formData.email}
                        onChange={(e) => updateField('email', e.target.value)}
                        className={`${isRTL ? 'pr-10' : 'pl-10'} h-12`}
                        dir="ltr"
                        data-testid="email-input"
                      />
                    </div>
                    {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                  </div>

                  {/* Password */}
                  <div className="space-y-2">
                    <Label htmlFor="password" className={isRTL ? 'text-right block' : ''}>
                      {t.signup.password}
                    </Label>
                    <div className="relative">
                      <Lock className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} w-5 h-5 text-muted-foreground`} />
                      <Input
                        id="password"
                        name="new-password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        placeholder={t.signup.passwordPlaceholder}
                        value={formData.password}
                        onChange={(e) => updateField('password', e.target.value)}
                        className={`${isRTL ? 'pr-10 pl-10' : 'pl-10 pr-10'} h-12`}
                        dir="ltr"
                        data-testid="password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className={`absolute top-3 ${isRTL ? 'left-3' : 'right-3'} text-muted-foreground hover:text-foreground`}
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
                    <p className="text-xs text-muted-foreground">{t.signup.passwordRequirements}</p>
                  </div>

                  {/* Confirm Password */}
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className={isRTL ? 'text-right block' : ''}>
                      {t.signup.confirmPassword}
                    </Label>
                    <div className="relative">
                      <Lock className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} w-5 h-5 text-muted-foreground`} />
                      <Input
                        id="confirmPassword"
                        name="confirm-password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        placeholder={t.signup.confirmPasswordPlaceholder}
                        value={formData.confirmPassword}
                        onChange={(e) => updateField('confirmPassword', e.target.value)}
                        className={`${isRTL ? 'pr-10' : 'pl-10'} h-12`}
                        dir="ltr"
                        data-testid="confirm-password-input"
                      />
                    </div>
                    {errors.confirmPassword && <p className="text-sm text-destructive">{errors.confirmPassword}</p>}
                  </div>

                  <Button 
                    type="submit" 
                    className={`w-full h-12 text-lg font-semibold mt-6 ${isRTL ? 'flex-row-reverse' : ''}`}
                    data-testid="next-step-button"
                  >
                    {language === 'ar' ? 'التالي' : 'Next: Company Info'}
                    {isRTL ? <ArrowLeft className="mr-2 w-5 h-5" /> : <ArrowRight className="ml-2 w-5 h-5" />}
                  </Button>
                </div>
              ) : (
                /* Step 2: Company Information */
                <div className="space-y-4">
                  {/* Company Name */}
                  <div className="space-y-2">
                    <Label htmlFor="company_name" className={isRTL ? 'text-right block' : ''}>
                      {t.signup.companyName}
                    </Label>
                    <div className="relative">
                      <Building2 className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} w-5 h-5 text-muted-foreground`} />
                      <Input
                        id="company_name"
                        placeholder={t.signup.companyNamePlaceholder}
                        value={formData.company_name}
                        onChange={(e) => updateField('company_name', e.target.value)}
                        className={`${isRTL ? 'pr-10 text-right' : 'pl-10'} h-12`}
                        data-testid="company-name-input"
                      />
                    </div>
                    {errors.company_name && <p className="text-sm text-destructive">{errors.company_name}</p>}
                  </div>

                  {/* Company Website */}
                  <div className="space-y-2">
                    <Label htmlFor="company_website" className={isRTL ? 'text-right block' : ''}>
                      {t.signup.companyWebsite}
                      {!formData.no_company_website && <span className="text-destructive ml-1">*</span>}
                    </Label>
                    <div className="relative">
                      <Globe className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} w-5 h-5 text-muted-foreground`} />
                      <Input
                        id="company_website"
                        placeholder={t.signup.companyWebsitePlaceholder}
                        value={formData.company_website}
                        onChange={(e) => updateField('company_website', e.target.value)}
                        className={`${isRTL ? 'pr-10 text-right' : 'pl-10'} h-12 ${formData.no_company_website ? 'opacity-50' : ''}`}
                        dir="ltr"
                        disabled={formData.no_company_website}
                        data-testid="company-website-input"
                      />
                    </div>
                    {/* URL format hint (only shown when toggle is OFF) */}
                    {!formData.no_company_website && (
                      <p className="text-xs text-muted-foreground" data-testid="company-website-hint">
                        {t.signup.companyWebsiteHint}
                      </p>
                    )}
                    {/* "I don't have a valid website" toggle */}
                    <div className={`flex items-center gap-2 pt-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <Checkbox
                        id="no_company_website"
                        checked={formData.no_company_website}
                        onCheckedChange={(checked) => {
                          updateField('no_company_website', !!checked);
                          if (checked) {
                            // Clear any website error when user opts out
                            setErrors(prev => ({ ...prev, company_website: undefined }));
                          }
                        }}
                        data-testid="no-company-website-checkbox"
                      />
                      <Label
                        htmlFor="no_company_website"
                        className="text-sm font-normal cursor-pointer select-none"
                      >
                        {t.signup.noWebsiteToggle}
                      </Label>
                    </div>
                    {formData.no_company_website && (
                      <p className="text-xs text-muted-foreground italic" data-testid="no-website-helper">
                        {t.signup.noWebsiteHelper}
                      </p>
                    )}
                    {errors.company_website && <p className="text-sm text-destructive">{errors.company_website}</p>}
                  </div>

                  {/* Company Country (worldwide) */}
                  <div className="space-y-2">
                    <Label htmlFor="company_country" className={isRTL ? 'text-right block' : ''}>
                      {t.signup.companyCountry}
                    </Label>
                    <Popover open={companyCountryOpen} onOpenChange={setCompanyCountryOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          role="combobox"
                          aria-expanded={companyCountryOpen}
                          className="h-12 w-full justify-between font-normal"
                          data-testid="company-country-select"
                        >
                          <span className={`flex items-center gap-2 truncate ${formData.company_country ? '' : 'text-muted-foreground'}`}>
                            <Globe className="w-4 h-4 shrink-0 text-muted-foreground" />
                            {formData.company_country
                              ? getCountryName(formData.company_country, language)
                              : t.signup.selectCountry}
                          </span>
                          <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                        <Command>
                          <CommandInput
                            placeholder={language === 'ar' ? 'ابحث عن بلد...' : 'Search country...'}
                            data-testid="company-country-search"
                          />
                          <CommandList>
                            <CommandEmpty>
                              {language === 'ar' ? 'لا توجد نتائج' : 'No country found.'}
                            </CommandEmpty>
                            <CommandGroup>
                              {countryOptions.map((c) => (
                                <CommandItem
                                  key={c.code}
                                  value={`${c.name} ${c.code}`}
                                  onSelect={() => {
                                    updateField('company_country', c.code);
                                    setCompanyCountryOpen(false);
                                  }}
                                  data-testid={`company-country-option-${c.code}`}
                                >
                                  <Check
                                    className={`w-4 h-4 ${
                                      formData.company_country === c.code ? 'opacity-100' : 'opacity-0'
                                    }`}
                                  />
                                  <span className="flex-1">{c.name}</span>
                                  <span className="text-xs text-muted-foreground">{c.code}</span>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    {errors.company_country && <p className="text-sm text-destructive">{errors.company_country}</p>}
                  </div>

                  {/* Primary Country of Interest (GCC) */}
                  <div className="space-y-2">
                    <Label htmlFor="primary_country" className={isRTL ? 'text-right block' : ''}>
                      {t.signup.primaryCountry}
                    </Label>
                    <Select
                      value={formData.primary_country}
                      onValueChange={(value) => updateField('primary_country', value)}
                    >
                      <SelectTrigger className="h-12" data-testid="primary-country-select">
                        <span className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-muted-foreground" />
                          <SelectValue placeholder={t.signup.selectPrimaryCountry} />
                        </span>
                      </SelectTrigger>
                      <SelectContent>
                        {GCC_COUNTRIES.map((code) => (
                          <SelectItem key={code} value={code} data-testid={`primary-country-option-${code}`}>
                            {t.signup.countries[code]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">{t.signup.primaryCountryHint}</p>
                    {errors.primary_country && <p className="text-sm text-destructive">{errors.primary_country}</p>}
                  </div>

                  {/* WO-53 — acceptance of the Terms, recorded by the server with the version and date */}
                  <div className="space-y-1">
                    <div className={`flex items-start gap-2 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                      <Checkbox
                        id="accept_terms"
                        checked={formData.accept_terms}
                        onCheckedChange={(checked) => updateField('accept_terms', !!checked)}
                        data-testid="accept-terms-checkbox"
                        className="mt-0.5"
                      />
                      <Label htmlFor="accept_terms" className="text-sm font-normal leading-snug cursor-pointer select-none">
                        {isRTL ? 'أوافق على ' : 'I agree to the '}
                        <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-primary underline">{isRTL ? 'شروط الخدمة' : 'Terms of Service'}</a>
                        {isRTL ? ' وقد قرأت ' : ' and have read the '}
                        <a href="https://www.mid2bid.com/privacy-policy/" target="_blank" rel="noopener noreferrer" className="text-primary underline">{isRTL ? 'سياسة الخصوصية' : 'Privacy Policy'}</a>
                        {isRTL ? '.' : '.'}
                      </Label>
                    </div>
                    {errors.accept_terms && <p className="text-sm text-destructive" data-testid="accept-terms-error">{errors.accept_terms}</p>}
                  </div>

                  <div className={`flex gap-3 mt-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Button 
                      type="button" 
                      variant="outline"
                      onClick={handleBack}
                      className={`h-12 px-6 ${isRTL ? 'flex-row-reverse' : ''}`}
                      data-testid="back-button"
                    >
                      {isRTL ? <ArrowRight className="ml-2 w-5 h-5" /> : <ArrowLeft className="mr-2 w-5 h-5" />}
                      {t.signup.back}
                    </Button>
                    <Button 
                      type="submit" 
                      className={`flex-1 h-12 text-lg font-semibold ${isRTL ? 'flex-row-reverse' : ''}`}
                      disabled={loading}
                      data-testid="create-account-button"
                    >
                      {loading ? t.signup.creating : t.signup.createAccount}
                      {!loading && <Check className={`${isRTL ? 'mr-2' : 'ml-2'} w-5 h-5`} />}
                    </Button>
                  </div>
                </div>
              )}
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                {t.signup.haveAccount}{' '}
                <Link to="/login" className="text-primary font-semibold hover:underline" data-testid="login-link">
                  {t.signup.login}
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
        <LegalFooter language={language} />
      </div>
    </div>
  );
}
